/**
 * Login.tsx — Chatter login page (T-04-32 / ARCH §10.3).
 *
 * Flow:
 *   1. POST /auth/login → sets httpOnly session cookie
 *   2. GET /auth/me verifies the session; both admin and chatter roles accepted
 *      (admins can also use this surface — the inbox is the chatter surface)
 *   3. On success → redirect to /inbox
 *
 * Security:
 *   - form autocomplete="current-password" for password field
 *   - Errors shown verbatim from the API (never echoes passwords back)
 */

import * as React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { authApi, ApiRequestError } from '../lib/api-client.js';
import { Button, Input, Card, CardHeader, CardTitle, CardContent, ThemeSwitch } from '@crm/ui';

export function Login() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();

  // One-time post-action banners (D-09-18 / SC-1 / SC-4)
  const isInvited = searchParams.get('invited') === '1';
  const isReset = searchParams.get('reset') === '1';

  const [email, setEmail] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [errorMsg, setErrorMsg] = React.useState<string | null>(null);

  const loginMut = useMutation({
    mutationFn: async () => {
      const me = await authApi.login({ email, password });
      // Both chatter and admin roles can use the chatter surface
      // (admin can observe the chatter view during support)
      return me;
    },
    onSuccess: (me) => {
      queryClient.setQueryData(['me'], me);
      navigate('/inbox', { replace: true });
    },
    onError: (err) => {
      if (err instanceof ApiRequestError) {
        setErrorMsg(
          typeof err.body.message === 'string'
            ? err.body.message
            : err.body.message.join('; '),
        );
      } else {
        setErrorMsg((err as Error).message ?? 'Login failed');
      }
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMsg(null);
    loginMut.mutate();
  };

  return (
    <div className="min-h-screen bg-bg flex items-center justify-center p-4 relative">
      <div className="absolute top-4 right-4">
        <ThemeSwitch />
      </div>
      <div className="w-full max-w-sm">
        <div className="mb-8 flex flex-col items-center text-center gap-2">
          <span className="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-accent text-white font-bold text-lg">
            M
          </span>
          <h1 className="text-xl font-bold text-text">MaloumDash</h1>
          <p className="text-sm text-text-muted">Chatter workspace</p>
        </div>

        {/* Post-action success banners (one-time, non-dismissible) */}
        {isInvited && (
          <p className="text-sm text-success mb-4 text-center" role="status">
            Account created. Sign in to continue.
          </p>
        )}
        {isReset && (
          <p className="text-sm text-success mb-4 text-center" role="status">
            Password reset successfully. Sign in with your new password.
          </p>
        )}

        <Card>
          <CardHeader>
            <CardTitle>Sign in</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="flex flex-col gap-4">
              <Input
                label="Email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={loginMut.isPending}
              />
              <Input
                label="Password"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={loginMut.isPending}
              />

              {errorMsg && (
                <p className="text-sm text-danger" role="alert">
                  {errorMsg}
                </p>
              )}

              <Button
                type="submit"
                loading={loginMut.isPending}
                className="w-full justify-center mt-2"
              >
                Sign in
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Forgot password link (SC-4 / D-09-18) */}
        <p className="text-center text-xs text-text-muted mt-3">
          <a href="/reset-password" className="text-accent hover:underline">
            Forgot password?
          </a>
        </p>
      </div>
    </div>
  );
}
